<template>
  <MtxPage>
    <MtxLoading v-if="isLoading" />

    <template v-else-if="tournament">
      <header class="grid grid-3-columns@md grid-gap align-center mb-5">
        <div class="column-span-2@md">
          <span v-tippy :title="status.description">
            <MtxBadge :variant="status.badge" class="mb-2">
              {{ status.text | capitalize }}
            </MtxBadge>
          </span>
          <h1 class="mb-0 break-word">{{ tournament.title }}</h1>
          <div class="color-muted break-word mb-1">
            {{ tournament.address }}
            <Copy plain :text="tournament.address" />
          </div>
          <div class="inline-flex align-center mr-3">
            by
            <ProfileLink :address="tournament.owner" copy class="ml-1" />
          </div>
          <router-link
            :to="`/tournaments?category=${tournament.category}`"
            v-if="tournament.category"
          >
            <MtxSvg icon="icon-books" />
            {{ tournament.category | capitalize }}
          </router-link>
        </div>

        <div class="tournament__bounties text-center px-2 py-2">
          <div v-if="tournament.round.status === 'closed'">
            <small>Total awarded:</small>
            <br />
            <span class="heading-1">{{ tournament.bounty | mtx }}</span>
          </div>

          <div v-else>
            <p class="mb-0">
              <small>Total Tournament Balance</small>
              <br />
              <span class="heading-5">{{ totalBalance | mtx }}</span>
            </p>
            <p>
              <small>Current Round Reward</small>
              <br />
              <span class="heading-1">{{ tournament.round.bounty | mtx }}</span>
            </p>
          </div>

          <BountyContributions :tournament="tournament" @chaChing="updateBounty" />
        </div>
      </header>

      <div class="grid grid-gap grid-3-columns@md">
        <MtxCard>
          <ul class="list-plain">
            <li class="heading-6">
              <MtxSvg v-if="tournament.round.totalParticipants === 1" icon="icon-person" />
              <MtxSvg v-else icon="icon-people" />
              {{ tournament.round.totalParticipants | pluralize("participant") }}
            </li>
            <li class="heading-6">
              <MtxSvg icon="icon-file" />
              {{ tournament.round.totalSubmissions | pluralize("submission") }}
            </li>
            <li class="heading-6">
              <template v-if="['open', 'scheduled'].includes(tournament.round.status)">
                <MtxSvg icon="icon-timelapse" />
                Round {{ tournament.round.status === "open" ? "ends" : "opens" }}
                {{ tournament.round.endDate | distanceInWordsToNow }}
                <br />
                <small class="color-muted ml-3">
                  <time :datetime="tournament.round.endDate">
                    {{ tournament.round.endDate | date("MMM Do, YYYY") }} at
                    {{ tournament.round.endDate | date("h:mma") }}
                  </time>
                </small>
              </template>
              <template v-else-if="tournament.round.status === 'review'">
                <MtxSvg icon="icon-eye" />
                Round is in review
              </template>
              <template v-else-if="tournament.round.status === 'abandoned'">
                <MtxSvg icon="icon-warning" />
                Round has been abandoned
              </template>
            </li>
          </ul>

          <button v-if="!web3.ready" @click="requireWeb3()" class="btn--block">
            Sign in to interact
          </button>

          <template v-else>
            <template v-if="tournament.round.status === 'open'">
              <RouterLink
                v-if="isEntrant"
                :to="{ name: 'CreateSubmission' }"
                class="btn btn--block"
              >
                Create Submission
              </RouterLink>

              <button v-else-if="!isOwner" @click="requireWeb3(enterTournament)" class="btn--block">
                Enter Tournament
                <template v-if="tournament.entryFee > 0">
                  ({{ tournament.entryFee | mtx }})
                </template>
              </button>

              <RouterLink
                v-if="isOwner"
                :to="{ name: 'TournamentsEdit', params: { tournamentAddress: tournament.address } }"
                class="btn btn--block"
              >
                Edit
              </RouterLink>
            </template>

            <RouterLink
              v-else-if="tournament.round.status === 'scheduled' && isOwner"
              :to="{ name: 'TournamentsEdit', params: { tournamentAddress: tournament.address } }"
              class="btn btn--block"
            >
              Edit
            </RouterLink>

            <template v-else-if="tournament.round.status === 'abandoned'">
              <button
                v-if="isEntrant && tournament.round.submissions.length"
                @click="requireWeb3(withdrawFromAbandoned)"
                class="btn--block"
              >
                Withdraw Abandoned Share
              </button>

              <button
                v-else-if="isOwner && !tournament.round.submissions.length && totalBalance"
                @click="requireWeb3(recoverBounty)"
                class="btn--block"
              >
                Recover Balance
              </button>
            </template>

            <button
              v-if="isEntrant"
              @click="requireWeb3(exitTournament)"
              class="btn--block btn--clear mt-1"
            >
              Exit Tournament
            </button>
          </template>
        </MtxCard>

        <MtxCard class="column-span-2@md">
          <h2>Description</h2>
          <p class="pre-wrap">{{ tournament.description }}</p>

          <a
            v-if="tournament.ipfsFiles"
            :href="`https://ipfs.infura.io/ipfs/${tournament.ipfsFiles}/`"
            target="_blank"
          >
            View files uploaded to IPFS
          </a>
        </MtxCard>

        <MtxCard class="column-span-3@md">
          <MtxTabs>
            <template #Rounds>
              <RoundDetails
                :tournament="tournament"
                :isOwner="isOwner"
                :mtxTournament="Tournament"
              />
            </template>
            <template #Discussion>
              <CommentsList
                referenceType="tournament"
                :referenceId="tournament.id"
                :disableComments="tournament.round.status !== 'open'"
              />
            </template>
          </MtxTabs>
        </MtxCard>
      </div>
    </template>
  </MtxPage>
</template>

<script>
import BountyContributions from "@/components/tournaments/BountyContributions"
import RoundDetails from "@/components/tournaments/RoundDetails"
import CommentsList from "@/components/comments/CommentsList"
import { MatryxTournament } from "@/web3/contracts"
import nprogress from "@/mixins/nprogress"
import web3 from "@/mixins/web3"
import API from "@/api"

const NETWORK_ID = process.env.VUE_APP_NETWORK_ID || "3"

const statusMap = {
  scheduled: {
    text: "Scheduled",
    badge: "warning",
    description: "The round is scheduled to start in the future"
  },
  open: {
    text: "Accepting Submissions",
    badge: "success",
    description: ""
  },
  closed: {
    text: "Tournament Closed",
    badge: "error",
    description: "This tournament has ended"
  },
  review: {
    text: "Round Under Review",
    badge: "warning",
    description: "Submissions are currently being reviewed"
  },
  hasWinners: {
    text: "Round Under Review",
    badge: "warning",
    description: "Submissions are currently being reviewed"
  },
  abandoned: {
    text: "Tournament Abandoned",
    badge: "grey",
    description:
      "The tournament either didn't get any submissions, or the owner failed to select winners during review"
  }
}

export default {
  mixins: [nprogress, web3],

  components: {
    BountyContributions,
    RoundDetails,
    CommentsList
  },

  props: {
    tournamentAddress: {
      type: String,
      required: true
    }
  },

  data: () => ({
    tournament: null,
    Tournament: null,
    isEntrant: false,
    bountyAdded: 0
  }),

  metaInfo() {
    return {
      titleTemplate: this.tournament ? `%s | ${this.tournament.title}` : "%s",
      meta: [
        {
          name: "description",
          content: this.tournament ? this.tournament.description : ""
        }
      ]
    }
  },

  computed: {
    isOwner() {
      return this.$store.state.user.ethAddress === this.tournament.owner
    },

    totalBalance() {
      return this.tournament.balance + this.bountyAdded
    },

    status() {
      return statusMap[this.tournament.round.status] || {}
    }
  },

  watch: {
    tournamentAddress: "fetchTournament",

    "$store.state.user.ethAddress": {
      async handler(account) {
        await this.fetchTournament()
        if (!account || this.web3.network !== NETWORK_ID) return

        if (!this.Tournament) {
          this.Tournament = new MatryxTournament(
            this.tournament.address,
            this.contracts.artifacts.tournament.abi
          )
        }

        this.isEntrant = await this.Tournament.isEntrant(account)
      },
      immediate: true
    }
  },

  methods: {
    async fetchTournament() {
      this.isLoading = true

      const { success, data, status } = await API.getTournament(this.tournamentAddress)
      if (!success) {
        if (status === 400) {
          return this.$router.push({ name: "Error400" })
        }
        if (status === 404) {
          return this.$router.replace({ name: "Error404", params: { resource: "tournament" } })
        }
        return this.$router.push({ name: "Error500" })
      }

      this.tournament = data.tournament
      this.isLoading = false
    },

    enterTournament() {
      const tx = this.addPlannedTx({
        txFn: () => this.Tournament.enter(),
        allowance: this.tournament.entryFee,
        header: "Enter Tournament",
        body: `This will ${
          this.tournament.entryFee
            ? `transfer a refundable entry fee of ${this.tournament.entryFee} MTX, and will`
            : ""
        } allow you to create submissions on this tournament.`
      })

      tx.then(() => (this.isEntrant = true))
    },

    exitTournament() {
      const tx = this.addPlannedTx({
        txFn: () => this.Tournament.exit(),
        header: "Exit Tournament",
        body:
          "You will be refunded any entry fee you paid, and will no longer be able to create submissions on this tournament."
      })

      tx.then(() => (this.isEntrant = false))
    },

    withdrawFromAbandoned() {
      const tx = this.addPlannedTx({
        txFn: () => this.Tournament.withdrawFromAbandoned(),
        header: "Withdraw Abandoned",
        body:
          "This will transfer your portion of the tournament's bounty, and refund any entry fee you have paid."
      })

      tx.then(() => (this.isEntrant = false))
    },

    updateBounty(amountAdded) {
      this.bountyAdded += +amountAdded

      this.tournament.contributions.push({
        contributor: this.web3.account,
        amount: +amountAdded
      })
    },

    recoverBounty() {
      const tx = this.addPlannedTx({
        txFn: () => this.Tournament.recoverBounty(),
        header: "Recover Bounty",
        body: "This will transfer the remaining tournament bounty to you."
      })

      tx.then(() => (this.bountyAdded = -this.tournament.balance))
    }
  }
}
</script>

<style lang="scss">
.tournament {
  &__bounties {
    border-radius: $borderRadius;
    background-color: rgba(#000, 0.3);
  }
}
</style>
